import React from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import { toCsv, downloadCsv } from '../../../Utilities/csvExport';

const CSV_COLUMNS = [
    { key: 'severity', header: 'Severity' },
    { key: 'row', header: 'Row' },
    { key: 'distributor', header: 'Distributor' },
    { key: 'sku', header: 'SKU' },
    { key: 'week', header: 'Week' },
    { key: 'amount', header: 'Amount' },
    { key: 'message', header: 'Message' },
];

const ImportSuccessDialog = ({ open, onClose, successData }) => {
    if (!successData) return null;

    const { message, errors = [], warnings = [] } = successData;

    const handleDownloadCsv = () => {
        const rows = [
            ...errors.map((issue) => ({ ...issue, severity: 'Error' })),
            ...warnings.map((issue) => ({ ...issue, severity: 'Warning' })),
        ];
        downloadCsv('forecast-import-results.csv', toCsv(rows, CSV_COLUMNS));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Forecast import result</DialogTitle>
            <DialogContent>
                <Typography variant="body1" paragraph>
                    {message}
                </Typography>
                {errors.length > 0 && (
                    <Accordion defaultExpanded sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Errors ({errors.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {errors.map((error, index) => (
                                    <ListItem key={index}>
                                        <ListItemText primary={error.message} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )}
                {warnings.length > 0 && (
                    <Accordion defaultExpanded={errors.length === 0} sx={{ mt: errors.length > 0 ? 1 : 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2">Warnings ({warnings.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {warnings.map((warning, index) => (
                                    <ListItem key={index}>
                                        <ListItemText primary={warning.message} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                ))}
                            </List>
                        </AccordionDetails>
                    </Accordion>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleDownloadCsv}
                    startIcon={<DownloadIcon />}
                    disabled={errors.length === 0 && warnings.length === 0}
                >
                    Download CSV
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

ImportSuccessDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    successData: PropTypes.shape({
        message: PropTypes.string,
        updatedCount: PropTypes.number,
        skippedCount: PropTypes.number,
        errors: PropTypes.arrayOf(
            PropTypes.shape({
                row: PropTypes.number,
                distributor: PropTypes.string,
                sku: PropTypes.string,
                week: PropTypes.string,
                amount: PropTypes.string,
                message: PropTypes.string,
            })
        ),
        warnings: PropTypes.arrayOf(
            PropTypes.shape({
                row: PropTypes.number,
                distributor: PropTypes.string,
                sku: PropTypes.string,
                week: PropTypes.string,
                amount: PropTypes.string,
                message: PropTypes.string,
            })
        ),
    }),
};

export default ImportSuccessDialog;
